<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppDialog.project-dialog(
    :key="show"
    append-to-body
    :loading="showDialogLoading"
    :show="show"
    :title="isNewForm ? 'New project' : 'Edit project'"
    @close="closeDialog"
  )
    header.mb-10(v-if="projectName")
      AppAvatarInitial(
        size="small"
        :text="projectName"
      )
      span {{ projectName }}
    AppTabs(
      v-if="!showDialogLoading"
      :selected-tab="selectedTab"
      compact
    )
      AppTab(
        label="General"
        tab-id="general"
        :has-error="validateTab('general')"
      )
        ValidationObserver(ref="general")
          ProjectDialogTabGeneral
      AppTab(
        v-if="membersEnabled"
        label="Members"
        tab-id="members"
      )
        ProjectDialogTabMembers
      AppTab(
        v-if="activeView"
        label="Budget & Limits"
        tab-id="budget"
        :has-error="validateTab('budget')"
      )
        ValidationObserver(ref="budget")
          ProjectDialogTabBudgets(
            :selected-toggle="selectedToggle"
            @clearedBudget="setConfirmClearBudget"
            @memberAdded="setAlertDesktopAppVersion"
          )
      AppTab(
        v-if="teamsVisible"
        label="Teams"
        tab-id="teams"
        :has-error="validateTab('teams')"
      )
        ValidationObserver(ref="teams")
          ProjectDialogTabTeams
    template(slot="footer")
      button.btn.btn-default(@click="closeDialog") Cancel
      button.btn.btn-primary(
        :disabled="submitting"
        @click="handleSubmit"
      )
        | {{ submitButtonLabel }}
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProjectDialogTabGeneral from './ProjectDialogTabGeneral.vue';
import ProjectDialogTabMembers from './ProjectDialogTabMembers.vue';
import ProjectDialogTabBudgets from './ProjectDialogTabBudgets.vue';
import ProjectDialogTabTeams from './ProjectDialogTabTeams.vue';
import { handleBudget } from '~/utils/budget_utils';
import { handleFormErrors } from '~/utils/form_utils';

const UPDATE_CLIENT_WARNING =
  'Warning: Time tracking will not automatically stop when the member limit is reached unless the member tracking time is running the latest version of the Hubstaff application.';
const CLEAR_BUDGET_CONFIRMATION_MESSAGE =
  "Removing this project's budget will remove its budget history. Are you sure?";

export default {
  name: 'ProjectsDialog',
  components: {
    ProjectDialogTabGeneral,
    ProjectDialogTabMembers,
    ProjectDialogTabBudgets,
    ProjectDialogTabTeams,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    selectedTab: {
      type: String,
      required: true,
    },
    selectedToggle: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    confirmClearBudget: false,
    alertDesktopAppVersion: false,
  }),
  computed: {
    ...mapState(['project', 'isNewForm', 'submitting', 'errors', 'budget']),
    ...mapGetters([
      'projectData',
      'projectName',
      'teamsVisible',
      'hasMultipleProjectNames',
      'submitButtonLabel',
      'membersEnabled',
      'showDialogLoading',
      'activeView',
    ]),
  },
  methods: {
    ...mapActions([
      'createProject',
      'loadTable',
      'setSubmitting',
      'resetFields',
      'updateField',
      'updateProject',
      'resetBudget',
    ]),
    closeDialog() {
      this.$emit('close');
      this.resetFields();
    },
    validateTab(tabId) {
      if (this.$refs.dialogObserver?.flags.invalid) {
        const tab = this.$refs[`${tabId}`];

        return tab?.flags.validated && tab.flags.invalid;
      }
    },
    async handleSubmit() {
      const validated = await this.$refs.dialogObserver.validate();

      if (validated) {
        await this.setSubmitting(true);

        if (this.isNewForm) {
          this.checkBudget(this.projectData);
          this.createProject(this.projectData).then(this.completeRequest, this.handleErrors);
        } else {
          this.showAlertForDesktopAppVersion();
          if (this.confirmClearBudget && this.project.budget && !this.project.budget.type) {
            this.submitWithClearBudgetConfirmation();
          } else {
            this.checkBudget(this.projectData);
            this.projectData.origin = 'update_project_modal';
            this.updateProject(this.projectData).then(this.completeRequest, this.handleErrors);
          }
        }
      }
    },
    showAlertForDesktopAppVersion() {
      if (this.alertDesktopAppVersion) {
        alert(UPDATE_CLIENT_WARNING);
      }
    },
    submitWithClearBudgetConfirmation() {
      if (confirm(CLEAR_BUDGET_CONFIRMATION_MESSAGE)) {
        this.updateField({ path: 'budget', value: null });
        this.updateProject(this.projectData).then(this.completeRequest, this.handleErrors);
      } else {
        this.setSubmitting(false);
      }
    },
    completeRequest(response) {
      if (response?.success) {
        this.handleSuccess(response);
      } else {
        this.handleErrors();
      }
    },
    handleSuccess(response) {
      let message;

      this.confirmClearBudget = false;
      this.alertDesktopAppVersion = false;
      if (this.isNewForm) {
        message = this.hasMultipleProjectNames ? 'Projects created' : 'Project created';
      } else {
        message = 'Project updated';
      }
      if (response.save_members_async) {
        message = `${message}. Members are being updated. This can take a couple of minutes. You will receive an email when this is complete.`;
      }
      this.$appMessage(message);
      this.closeDialog();
      this.loadTable();
    },
    handleErrors() {
      handleFormErrors(this.$refs.dialogObserver, this.errors, this.$appMessage);
      if (!this.project.budget) {
        this.resetBudget();
      }
    },
    setAlertDesktopAppVersion() {
      this.alertDesktopAppVersion = true;
    },
    setConfirmClearBudget() {
      this.confirmClearBudget = true;
    },
    checkBudget(projectData) {
      const budget = this.project.budget;
      let projectDataCheck;
      let projects;

      if (Array.isArray(projectData)) {
        projectDataCheck = projectData[0].budget;
        projects = projectData;
      } else {
        projectDataCheck = projectData.budget;
        projects = [projectData];
      }

      if (!handleBudget(this.budget, projectDataCheck, budget)) {
        projects.forEach((project) => {
          delete project.budget;
        });
      }
    },
  },
};
</script>
